import React, { useState } from 'react';
import { Send, CheckCircle, Shield, Video, Flame } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const QuoteForm = ({ category, products = [], simplified = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    products_interested: [],
    project_type: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const projectTypes = [
    { id: 'alarme', name: 'Alarme', icon: Shield, color: 'amber' },
    { id: 'video', name: 'Vidéoprotection', icon: Video, color: 'blue' },
    { id: 'ssi', name: 'SSI Incendie', icon: Flame, color: 'red' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProjectTypeSelect = (typeId) => {
    setFormData(prev => ({ ...prev, project_type: typeId }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          category: formData.project_type || category
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi');
      }

      setSubmitted(true);
    } catch (error) {
      console.error('Error:', 'Erreur lors de l\'envoi de la demande');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-[#0a0a0a] border border-white/5 rounded-2xl p-8 text-center" id="quote">
        <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-emerald-400" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">Demande envoyée !</h3>
        <p className="text-white/60 text-sm mb-6">
          Notre équipe vous contactera dans les plus brefs délais pour établir votre devis personnalisé.
        </p>
        <Button 
          onClick={() => setSubmitted(false)} 
          variant="outline"
          className="border-white/20 text-white hover:bg-white hover:text-black rounded-full px-6"
        >
          Nouvelle demande
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-[#0a0a0a] border border-white/5 rounded-2xl p-6 md:p-8" id="quote">
      <h3 className="text-xl font-semibold text-white mb-2">
        Demande de devis
      </h3>
      <p className="text-white/60 text-sm mb-6">
        Recevez un devis personnalisé sous 24h.
      </p>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Project Type Selection */}
        <div className="space-y-3">
          <Label className="text-white/60 text-xs uppercase tracking-wider">
            Type de projet *
          </Label>
          <div className="grid grid-cols-3 gap-2">
            {projectTypes.map((type) => {
              const isSelected = formData.project_type === type.id;
              const colorClasses = {
                amber: isSelected ? 'border-amber-500/50 bg-amber-500/10' : '',
                blue: isSelected ? 'border-blue-500/50 bg-blue-500/10' : '',
                red: isSelected ? 'border-red-500/50 bg-red-500/10' : '',
              };
              const iconClasses = {
                amber: isSelected ? 'text-amber-400' : 'text-white/50',
                blue: isSelected ? 'text-blue-400' : 'text-white/50',
                red: isSelected ? 'text-red-400' : 'text-white/50',
              };
              
              return (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => handleProjectTypeSelect(type.id)}
                  className={`p-4 rounded-xl border text-center transition-all ${
                    isSelected
                      ? colorClasses[type.color]
                      : 'border-white/10 bg-black hover:border-white/20'
                  }`}
                  data-testid={`quote-type-${type.id}`}
                >
                  <type.icon className={`w-5 h-5 mx-auto mb-2 ${iconClasses[type.color]}`} />
                  <span className={`text-xs font-medium ${isSelected ? 'text-white' : 'text-white/60'}`}>
                    {type.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-white/60 text-xs uppercase tracking-wider">
              Nom complet *
            </Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Jean Dupont"
              className="bg-black border-white/10 text-white placeholder:text-white/50 focus:border-white/20"
              data-testid="quote-name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className="text-white/60 text-xs uppercase tracking-wider">
              Téléphone *
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              required
              placeholder="06 12 34 56 78"
              className="bg-black border-white/10 text-white placeholder:text-white/50 focus:border-white/20"
              data-testid="quote-phone"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-white/60 text-xs uppercase tracking-wider">
            Email *
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="jean@exemple.com"
            className="bg-black border-white/10 text-white placeholder:text-white/50 focus:border-white/20"
            data-testid="quote-email"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="company" className="text-white/60 text-xs uppercase tracking-wider">
            Entreprise
          </Label>
          <Input
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            placeholder="Nom de l'entreprise"
            className="bg-black border-white/10 text-white placeholder:text-white/50 focus:border-white/20"
            data-testid="quote-company"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="message" className="text-white/60 text-xs uppercase tracking-wider">
            Décrivez votre projet
          </Label>
          <Textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={3}
            placeholder="Type de bâtiment, surface, besoins spécifiques..."
            className="bg-black border-white/10 text-white placeholder:text-white/50 focus:border-white/20 resize-none"
            data-testid="quote-message"
          />
        </div>

        <Button 
          type="submit"
          disabled={loading || !formData.project_type}
          className="w-full bg-white text-black hover:bg-white/90 h-12 text-base font-medium rounded-full disabled:opacity-50"
          data-testid="quote-submit"
        >
          {loading ? (
            'Envoi en cours...'
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Demander un devis
            </>
          )}
        </Button>
      </form>
    </div>
  );
};

export default QuoteForm;
